import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { createPracticeTournament } from '../lib/demos.js'
import { buildDemoTournament } from '../utils/aiActionExecutor.js'
import { db } from '../lib/firebase.js'

// Practice mode launcher — sandbox tournaments that look and behave like
// real ones (same scoring engine, same TV view, same admin tabs) but are
// flagged isDemo:true and info.practice:true. The PRACTICE banner inside
// the tournament makes the sandbox state obvious. Admin can edit, score,
// reset, or delete any practice tournament without touching real data.
//
// Three modes: 4-Ball, Scramble, Stroke (last one is "Coming Soon").

const MODES = [
  {
    key: 'practice4Ball',
    icon: '🏌️',
    label: 'Practice 4-Ball Setup',
    desc: 'Walk through creating a 4-Ball Match Play tournament with sample teams pre-filled.',
  },
  {
    key: 'practiceScramble',
    icon: '🌀',
    label: 'Practice Scramble Setup',
    desc: 'Walk through a 4-Man Scramble — shotgun assignment, side contests, leaderboard.',
  },
  {
    key: 'practiceStroke',
    icon: '📊',
    label: 'Practice Stroke Play Setup',
    desc: 'Individual stroke play with handicaps and leaderboard.',
    comingSoon: true,
  },
]

export default function PracticeLauncher({ buttonClass = 'btn-secondary text-sm' }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [flights, setFlights] = useState(12) // 4-Ball practice field size, 1–13
  const navigate = useNavigate()

  const launch = async (key) => {
    setBusy(true)
    setErr('')
    try {
      // 4-Ball practice builds a full field at the chosen size (Demo + Practice
      // flagged). Other modes keep their fixed sandbox roster.
      if (key === 'practice4Ball') {
        const result = await buildDemoTournament(db, {
          mode: 'fullTournament',
          size: flights * 8,
          practice: true,
        })
        if (!result?.success) { setErr(result?.message || 'Practice setup failed.'); return }
        setOpen(false)
        navigate(`/admin/t/${result.tournamentId}`)
        return
      }
      const { tournamentId } = await createPracticeTournament(key)
      setOpen(false)
      // Drop admin straight into the practice tournament so the walkthrough
      // is one click away. The PRACTICE banner there explains the mode.
      navigate(`/admin/t/${tournamentId}`)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className={buttonClass}
        onClick={() => setOpen(true)}
        title="Walk through tournament setup with sample data">
        ✨ Start Practice Setup
      </button>
      {open ? (
        <PracticeModal
          onClose={() => setOpen(false)}
          launch={launch}
          busy={busy}
          err={err}
          flights={flights}
          setFlights={setFlights}
        />
      ) : null}
    </>
  )
}

function PracticeModal({ onClose, launch, busy, err, flights, setFlights }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto"
      onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-5 my-8"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start gap-3">
          <div>
            <h3 className="text-xl font-extrabold">Practice Setup</h3>
            <p className="text-sm text-gray-600 mt-1">
              Pick a tournament type to walk through. Sample data is pre-filled — edit
              anything, score holes, try the TV view, then delete and start over when done.
              <span className="font-bold"> Nothing here affects your real tournaments.</span>
            </p>
          </div>
          <button
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            onClick={onClose}
            aria-label="Close">×</button>
        </div>

        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-widest font-extrabold text-gray-500">
            4-Ball field size
          </label>
          <select
            value={flights}
            disabled={busy}
            onChange={(e) => setFlights(Number(e.target.value))}
            className="input mt-1">
            {Array.from({ length: 13 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n} flight{n === 1 ? '' : 's'} — {n * 8} teams</option>
            ))}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => m.comingSoon ? null : launch(m.key)}
              disabled={busy || m.comingSoon}
              title={m.comingSoon ? 'Coming soon' : `Launch ${m.label}`}
              className={`text-left rounded-xl px-4 py-3 transition ${
                m.comingSoon
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-green-deep text-white hover:opacity-90 disabled:opacity-60'
              }`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xl">{m.icon}</span>
                {m.comingSoon ? (
                  <span className="text-[10px] uppercase tracking-widest font-extrabold rounded-full px-2 py-0.5"
                        style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                    Coming Soon
                  </span>
                ) : null}
              </div>
              <div className="font-extrabold text-sm leading-tight mt-1.5">{m.label}</div>
              <div className={`text-xs leading-snug mt-1 ${m.comingSoon ? 'text-gray-500' : 'text-white/80'}`}>
                {m.desc}
              </div>
            </button>
          ))}
        </div>

        {err ? <p className="text-sm text-red-600 mt-3">{err}</p> : null}

        <div className="mt-5 pt-4 border-t text-xs text-gray-500">
          Practice tournaments are tagged <b>PRACTICE</b> and live in the Demos &amp; Practice
          section. Delete any time from the list — the trash icon on the card.
        </div>
      </div>
    </div>
  )
}
